import { Button, Flex, Layout, Modal, Space, Typography, message } from 'antd';
import _ from 'lodash';
import Sider from 'antd/es/layout/Sider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faMinus, faPlus, faStreetView, faLocationCrosshairs } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import * as turf from '@turf/turf';
import maplibregl from 'maplibre-gl';
import HKUSTMap from '../components/HKUSTMap';
import hkustLogoLabel from '../images/hkust-logo-colored-label.svg';
import GeneralHelper from '../helpers/general';
import FloorSelector from '../components/FloorSelector';
import PointOfInterestSelector from '../components/PointOfInterestSelector';
import useIsMobile from '../helpers/customHooks/useIsMobile';
import useIndoorPositioning from '../helpers/customHooks/useIndoorPositioning';
import { IndoorPositioningProvider } from '../contexts/IndoorPositioning';
import { collapseDrawer } from '../helpers/drawerControl';
import { getAllBaseMap, getLocations, getBuildings, getLocationsByType, getPanoramaNodes, getPanoramaEdges, resetPage as resetMapPage } from '../actions/MapPage';
import { resetPage as resetLocationDetailPage } from '../actions/LocationDetailPage';
import LocationSearch from '../components/LocationSearch';
import MapDefaults from '../constants/MapDefaults';
import LocationPopup from '../components/LocationPopup';
import StreetViewPegman from '../components/StreetViewPegman';

import resetNorthIcon from '../images/reset-north.png';
const { Link } = Typography;
const { Content } = Layout;

// Keyed so the "detecting" toast can be replaced/dismissed rather than stacking up.
const LOCATING_MESSAGE_KEY = 'indoor-positioning-locating';
// Same, for the "this floor has no 360 imagery" notice — switching floors can raise it repeatedly.
const NO_PANORAMA_MESSAGE_KEY = 'street-view-no-coverage';
// Street view mode lives in the URL rather than in component state so that it survives leaving the
// map: opening a panorama pushes /street-view on top of a map entry that still says the graph was
// on, and coming back restores it — floor, viewport and all — instead of dropping the user onto a
// bare floor plan. See `handleGoBack` in StreetViewPage.
const STREET_VIEW_QUERY_KEY = 'street_view';

const MapLayout = ({ children }) => {
	const { location_details, loading_details, location_building_floors, building_floor, loading_building_floor, nav_nodes } = useSelector(state => state.locationDetailPage.locationDetailPageReducer) || {};
	const { directions, loading_directions } = useSelector(state => state.locationDetailPage.directionsPageReducer) || {};
	const { all_base_map, loading, locations, point_of_interests, buildings, panorama_nodes, panorama_edges, panorama_nodes_building_floor_id, loading_panorama_nodes } = useSelector(state => state.mapPageReducer) || {};
	const { filteredLocations, loadingLocType } = useSelector(state => state.mapPageReducer) || {};

	const dispatch = useDispatch();

	const history = useHistory();
	const location = useLocation();

	const mapRef = useRef({});
	const timeout = useRef(null);
	const streetViewModeRef = useRef(false);
	const isMobile = useIsMobile();

	const query = GeneralHelper.getParamsFromUrl();
	const { keyword } = query || {};

	// Indoor positioning (only active inside a native shell that ships the SDK). Run once here and shared
	// with everything below through IndoorPositioningProvider - DirectionsPage needs the same
	// session to offer "Current Location", and a second `useIndoorPositioning()` would be a
	// second, independent one. On by default: the blue dot is there without being asked for.
	const positioning = useIndoorPositioning({ autoStart: true });
	const {
		available: positioningAvailable,
		position: userLocation,
		heading: userHeading,
		loading: positioningLoading,
		error: positioningError,
		start: startPositioning,
		clearError: clearPositioningError
	} = positioning;

	// Set while we wait for the user's floor plan to be on screen before centring on them.
	const [centreOnUser, setCentreOnUser] = useState(false);
	// Set by a tap that had no fix to show yet, so the fix it starts off centres the map
	// when it lands. Later fixes (the user walking around) leave the map alone.
	const pendingLocateRef = useRef(false);
	// Whether the first fix has been dealt with. Positioning finding the user on its own only moves
	// the camera for that one (see the effect below); every later fix just moves the dot.
	const firstFixHandledRef = useRef(false);
	// The page "locate me" opened (or found already open) to show the user's floor:
	// { pathname, id, building_floor_id }. While the map still shows it, the map follows the user
	// from floor to floor; once they open anything else themselves it is cleared, and the map is
	// left alone until they tap "locate me" again.
	const followedFloorRef = useRef(null);
	const pointOfInterestsRef = useRef(null);
	const contentPaneRef = useRef(null);
	// The location whose own floor we have already opened, so that a location arriving from the
	// store on mount is told apart from one the user just opened. Null until the first sighting.
	const floorAppliedForRef = useRef(null);
	// The building on screen, so that moving to a different one can be told from moving between its
	// floors. Null until the first sighting.
	const shownBuildingRef = useRef(null);

	const isDirectionsPage = location.pathname.split('/').filter(part => part).includes('directions');

	// The home screen as the app opens on it: nothing searched for, filtered or opened. `debug` is
	// only ever carried along by the router.
	const isUntouchedHome = () => location.pathname === '/' && Object.keys(query).every(key => key === 'debug');

	// Open the floor the user is standing on, and follow them from it (see followedFloorRef). The
	// plan is loaded by LocationDetailPage from `id` (the building) + `building_floor_id`, so both
	// have to be in the query: `building_floor_id` on its own renders a plan with no floor selector.
	//
	// A route stays open: on the directions page only the floor changes, the way the floor selector
	// changes it there, and the route rather than the user's position drives the floor after that.
	const showUserFloor = (loc) => {
		if (!loc || !GeneralHelper.isObjectId(loc.building_floor_id)) return;

		if (isDirectionsPage) {
			if (loc.building_floor_id !== query.building_floor_id) {
				history.push(`${location.pathname}?${GeneralHelper.objectToQueryString({ ...query, building_floor_id: loc.building_floor_id })}`);
			}
			return;
		}

		// Already showing it - on a room of that floor, say - so there is nothing to open, and the
		// page on screen is the one to follow the user from.
		if (loc.building_floor_id === query.building_floor_id) {
			followedFloorRef.current = { pathname: location.pathname, id: query.id, building_floor_id: loc.building_floor_id };
			return;
		}

		const queryObject = {
			...query,
			...(GeneralHelper.isObjectId(loc.building_id) && { id: loc.building_id }),
			building_floor_id: loc.building_floor_id
		};
		followedFloorRef.current = { pathname: '/location/id', id: queryObject.id, building_floor_id: loc.building_floor_id };
		history.push(`/location/id?${GeneralHelper.objectToQueryString(queryObject)}`);
	};

	// How far the middle of the map the user can actually see sits from the middle of the map
	// itself, in pixels. On phones the search field and category chips float over the top of the
	// map and the bottom sheet covers its bottom, so the true centre lands close to the sheet's edge.
	const getVisibleMapOffset = () => {
		const map = mapRef.current?.map;
		if (!map) return [0, 0];

		const mapRect = map.getContainer().getBoundingClientRect();
		const chipsRect = pointOfInterestsRef.current?.getBoundingClientRect();
		const sheetRect = contentPaneRef.current?.querySelector('.ant-drawer-content-wrapper')?.getBoundingClientRect();

		const top = Math.max(mapRect.top, chipsRect ? chipsRect.bottom : mapRect.top);
		const bottom = Math.min(mapRect.bottom, sheetRect ? sheetRect.top : mapRect.bottom);
		if (bottom <= top) return [0, 0];

		return [0, Math.round((top + bottom) / 2 - (mapRect.top + mapRect.bottom) / 2)];
	};

	// Tapping the "locate me" button:
	//  - with no fix yet, asks for one (button spinner + "Detecting…" toast) - joining the
	//    attempt positioning started on its own if it is still under way - and remembers to
	//    centre the map once the position lands;
	//  - once we have a fix, taps re-open the user's floor, recentre on them and follow them.
	// Never gated on being connected to the positioning server: a tap after a failed or
	// fruitless attempt has to be able to try again.
	const handleLocateMe = () => {
		if (positioningLoading) return;
		// Even with a fix in hand: the user is asking now, so what goes wrong from here is explained.
		startPositioning();
		if (userLocation) {
			showUserFloor(userLocation);
			setCentreOnUser(true);
			return;
		}
		pendingLocateRef.current = true;
	};

	// Non-blocking "we're working on it" indicator. Deliberately not a full-screen
	// spinner: detecting can take up to 20s and the map stays usable throughout.
	useEffect(() => {
		if (!positioningLoading) {
			message.destroy(LOCATING_MESSAGE_KEY);
			return undefined;
		}
		message.open({
			key: LOCATING_MESSAGE_KEY,
			type: 'loading',
			content: 'Detecting your location…',
			duration: 0,
			// Clear of the search field and the category chips above the map on mobile.
			style: { marginTop: 112 }
		});
		return () => message.destroy(LOCATING_MESSAGE_KEY);
	}, [positioningLoading]);

	// Show the "positioning not available" error as a dismissable modal.
	useEffect(() => {
		if (!positioningError) return;
		Modal.error({
			title: 'Indoor positioning',
			content: positioningError,
			okText: 'OK',
			onOk: clearPositioningError
		});
	}, [positioningError, clearPositioningError]);

	const [buildingFloor, setBuildingFloor] = useState({});
	const [lngLat, setLngLat] = useState(null);
	const [locationCoordinates, setLocationCoordinates] = useState(null);
	const [locationId, setLocationId] = useState(null);
	const [pointOfInterestId, setPointOfInterestId] = useState(null);
	const [showLocationSearch, setShowLocationSearch] = useState(false);
	const [tempRoutes, setTempRoutes] = useState(null);
	const [zoomLevel, setZoomLevel] = (useState(null));
	const [popupComponent, setPopupComponent] = useState(null);

	// Derived from the URL, not held in state: the map layout is remounted whenever the user comes
	// back from a full-screen page, and state would come back false.
	const streetViewMode = query?.[STREET_VIEW_QUERY_KEY] === '1';
	// While the person is being dragged out of the 360 button the floor's 360 points are drawn too,
	// the way Google Maps lights up its coverage while Pegman is held — without switching the mode
	// on, so a drag that is put back leaves the map as it was.
	const [pegmanDragging, setPegmanDragging] = useState(false);
	const showPanoramaCoverage = streetViewMode || pegmanDragging;

	// `handleMapOnClick` is memoised against the map instance and so can't read `streetViewMode`
	// directly without going stale.
	useEffect(() => {
		streetViewModeRef.current = streetViewMode;
	}, [streetViewMode]);

	// The floor whose plan is on screen. The URL is the source of truth: `building_floor` still
	// holds the previous floor while the newly selected one's plan is loading.
	const activeBuildingFloorId = GeneralHelper.isObjectId(query?.building_floor_id)
		? query.building_floor_id
		: building_floor?._id;

	// Transform panorama_nodes to match nav_nodes structure for rendering (render as simple circles)
	const transformedPanoramaNodes = useMemo(() => {
		if (!activeBuildingFloorId || !Array.isArray(panorama_nodes)) return null;

		return panorama_nodes
			// The floor just left keeps its nodes in the store until the new floor's request lands,
			// and two quick floor switches can resolve out of order — either way, only the floor on
			// screen may draw.
			.filter(node => String(node.building_floor_id) === String(activeBuildingFloorId))
			.map(node => ({
				_id: node._id,
				name: node.name || '',
				type_name: 'Panorama Node',
				type_display_setting: 'none', // Render as simple circles without icons
				longitude: parseFloat(node.longitude),
				latitude: parseFloat(node.latitude),
				building_floor_id: node.building_floor_id,
				building_id: node.building_id,
				show_at_zoom_level: 15, // Show at lower zoom levels
				minZoom: 15
			}));
	}, [panorama_nodes, activeBuildingFloorId]);

	// Transform panorama_edges - create a GeoJSON FeatureCollection with individual LineString features
	const transformedPanoramaEdges = useMemo(() => {
		if (!activeBuildingFloorId || !Array.isArray(panorama_edges) || panorama_edges.length === 0) return null;

		// Same staleness guard as the nodes above; edges are fetched by their from-node's floor.
		const floorEdges = panorama_edges.filter(edge =>
			String(edge.from_panorama_node_building_floor_id) === String(activeBuildingFloorId)
		);

		if (!floorEdges.length) return null;

		// Create a separate LineString feature for each edge
		const features = floorEdges.map(edge => ({
			type: 'Feature',
			geometry: {
				type: 'LineString',
				coordinates: [
					[parseFloat(edge.from_panorama_node_longitude), parseFloat(edge.from_panorama_node_latitude)],
					[parseFloat(edge.to_panorama_node_longitude), parseFloat(edge.to_panorama_node_latitude)]
				]
			},
			properties: {
				_id: edge._id,
				from_panorama_node_id: edge.from_panorama_node_id,
				to_panorama_node_id: edge.to_panorama_node_id
			}
		}));

		return {
			type: 'FeatureCollection',
			features: features
		};
	}, [panorama_edges, activeBuildingFloorId]);

	const handleSwitchFloor = (id) => {
		const pathnames = location.pathname.split('/').filter(part => part);
		const findPathInPaths = (paths, path) => paths.find(item => item === path);
		const target_path = findPathInPaths(pathnames, 'directions');

		const queryObject = { ...query, ...(!target_path && { id: location_details.building_id }), building_floor_id: id };
		const queryString = GeneralHelper.objectToQueryString(queryObject);
		history.push(`${location.pathname}?${queryString}`);

		// Reset marker, coordinates, lngLat, locationId, and pointOfInterestId when switching floor
		setLocationCoordinates(null);
		setLocationId(null);
		setPointOfInterestId(null);
		setLngLat(null);
		setPopupComponent(null);
	};

	const openPanorama = useCallback((panoramaNodeId) => {
		const queryString = GeneralHelper.objectToQueryString({ id: panoramaNodeId });
		// `fromMap` tells the 360 page it was opened from the map and may simply go back —
		// returning to the graph the user was looking at rather than rebuilding an
		// approximation of it. Absent when the URL was opened cold (a shared link).
		history.push({
			pathname: '/street-view',
			search: `?${queryString}`,
			state: { fromMap: true }
		});
	}, [history]);

	const handleMapOnClick = useCallback((e) => {
		const feature = e.features[0];
		const { properties } = feature || {};

		setLocationId(null);
		setPointOfInterestId(null);
		setPopupComponent(null);

		if (e.type === 'click') {
			// Handle panorama node click in street view mode
			if (streetViewModeRef.current && properties && properties._id && properties.type_name === 'Panorama Node') {
				openPanorama(properties._id);
				return;
			}

			if (properties && properties.location_id) {
				setLocationId(properties.location_id);
			} else {
				setPointOfInterestId(properties.point_of_interest_id);
			}
		} else if (e.type === 'contextmenu') {
			setPopupComponent(
				<LocationPopup
					locationId={properties.location_id}
					pointOfInterestId={properties.point_of_interest_id || properties._id}
					remoteId={properties.remote_id}
					history={history}
				/>
			);
		}
	}, [history, openPanorama]);

	// FIXME handleBaseMapOnClick
	const handleBaseMapOnClick = (e) => {
		clearTimeout(timeout.current);
		timeout.current = setTimeout(() => {

			if (!mapRef.current.map || !mapRef.current.map.isStyleLoaded()) return;

			// Check if a nav_nodes feature was clicked
			const navNodeFeatures = mapRef.current.map.queryRenderedFeatures(e.point, {
				layers: ['layer_nav_nodes', 'layer_nav_nodes_label', 'layer_nav_nodes_circles'].filter(layer => mapRef.current.map.getLayer(layer))
			});

			if (navNodeFeatures.length > 0) {
				// If a nav_node was clicked, skip base map processing
				console.log('Skipping base map processing due to nav_nodes click');
				return;
			}

			// Build the list of layers to query
			const availableLayers = ['layer_geojson', 'layer_base_geojson'].filter(layer =>
				mapRef.current.map.getLayer(layer)
			);
			if (mapRef.current.map.getLayer('layer_canteens')) {
				availableLayers.push('layer_canteens');
			}

			if (mapRef.current.map.getLayer('layer_aeds')) {
				availableLayers.push('layer_aeds');
			}

			if (mapRef.current.map.getLayer('layer_lift_shafts')) {
				availableLayers.push('layer_lift_shafts');
			}

			const features = mapRef.current.map.queryRenderedFeatures(e.point, {
				layers: availableLayers
			});

			const layer_geojson = features.find((f) => f.layer.id === 'layer_geojson');
			const layer_canteens = features.find((f) => f.layer.id === 'layer_canteens');
			const layer_aeds = features.find((f) => f.layer.id === 'layer_aeds');
			const layer_lift_shafts = features.find((f) => f.layer.id === 'layer_lift_shafts');
			const layer_base_geojson = features.find((f) => f.layer.id === 'layer_base_geojson');

			if (layer_geojson) return;
			setLocationId(null);
			setPointOfInterestId(null);
			setPopupComponent(null);

			if (layer_canteens && layer_canteens.properties) {
				const { properties } = layer_canteens;
				if (properties.location_id) {
					setLocationId(properties.location_id);
				} else if (properties.point_of_interest_id) {
					setPointOfInterestId(properties.point_of_interest_id);
				}
				return;
			}

			if (layer_aeds && layer_aeds.properties) {
				const { properties } = layer_aeds;
				if (properties.location_id) {
					setLocationId(properties.location_id);
				} else if (properties.point_of_interest_id) {
					setPointOfInterestId(properties.point_of_interest_id);
				}
				return;
			}

			if (layer_lift_shafts && layer_lift_shafts.properties) {
				const { properties } = layer_lift_shafts;
				if (properties.location_id) {
					setLocationId(properties.location_id);
				} else if (properties.point_of_interest_id) {
					setPointOfInterestId(properties.point_of_interest_id);
				}
				return;
			}

			if (layer_base_geojson && layer_base_geojson.properties) {
				const { properties } = layer_base_geojson;
				setLocationId(properties.building_id);
			}
		}, 200);
	};

	const handleZoom = (zoom, center = lngLat) => {
		if (mapRef.current) {
			const currentZoom = mapRef.current.map.getZoom();
			setZoomLevel(currentZoom);
			// if (longitude && latitude)
			// 	center = [longitude, latitude];
			// if (mapRef.current.centroid)
			// 	center = mapRef.current.centroid;

			mapRef.current.map.flyTo({
				center,
				zoom: currentZoom + zoom || 1,
				duration: 300
			});
		}
	};

	// `replace`, not `push`: the toggle is a view option on the page the user is already on, and the
	// entry it rewrites is the one /street-view is pushed on top of — which is what makes the 360
	// page's back button land back on the graph.
	const setStreetView = (on) => {
		// Read the URL as it stands rather than from this render's closure. Effects below call this
		// from the same commit as a navigation, where the captured `query` and `pathname` are one
		// step behind and would undo the move that just happened.
		const queryObject = GeneralHelper.getParamsFromUrl();

		if (on) queryObject[STREET_VIEW_QUERY_KEY] = '1';
		else delete queryObject[STREET_VIEW_QUERY_KEY];

		history.replace(`${history.location.pathname}?${GeneralHelper.objectToQueryString(queryObject)}`);
	};

	const handleStreetViewClick = () => {
		// The panorama graph for the floor on screen is fetched by the effect below, which also
		// keeps it in step when the user switches floor while the mode stays on.
		setStreetView(!streetViewMode);
	};

	// Only a drag asks for the floor's 360 points, for the same reason the toggle does not: the API
	// is not asked about every floor a user merely looks at. Street view mode already keeps them
	// loaded, and a floor whose answer is in need not be asked again for every drag.
	const handlePegmanDragStart = () => {
		setPegmanDragging(true);
		if (streetViewMode || !activeBuildingFloorId || panorama_nodes_building_floor_id === activeBuildingFloorId) return;

		dispatch(getPanoramaNodes({ building_floor_id: activeBuildingFloorId }));
		dispatch(getPanoramaEdges({ building_floor_id: activeBuildingFloorId }));
	};

	const handlePegmanMiss = () => {
		// A floor with no imagery at all has already said so the moment the drag began.
		if (panorama_nodes_building_floor_id === activeBuildingFloorId && !transformedPanoramaNodes?.length) return;

		const loaded = !loading_panorama_nodes && panorama_nodes_building_floor_id === activeBuildingFloorId;
		message.open({
			key: NO_PANORAMA_MESSAGE_KEY,
			type: 'info',
			content: loaded ? 'No 360° view here. Drop onto a blue dot.' : 'Still loading 360° views, try again',
			// Clear of the search field and the category chips above the map on mobile.
			style: { marginTop: 112 }
		});
	};

	const handleGoBack = () => {
		dispatch(resetMapPage());
		dispatch(resetLocationDetailPage());
		dispatch(getBuildings({ page: 1, limit: 20 }));
		setLocationCoordinates(null);
		setLocationId(null);
		setPointOfInterestId(null);
		setPopupComponent(null);
		history.push('/');
	};

	const handlePoiClick = (keyword) => {
		const searchKeys = {};

		if (keyword === 'Canteen' || keyword === 'AED' || keyword === 'Lift Shaft') {
			if (location_details && location_details.building_id) {
				searchKeys.id = location_details.building_id;
			}
			if (query && query.building_floor_id) {
				searchKeys.building_floor_id = query.building_floor_id;
			}
		}

		searchKeys.keyword = keyword;

		const queryString = GeneralHelper.objectToQueryString(searchKeys);
		dispatch(resetMapPage());
		if (keyword === 'Staff Quarter' || keyword === 'Student Hall') {
			dispatch(resetLocationDetailPage());
		}
		history.push(`/?${queryString}`);
	};

	const handleSelectOnChange = (e) => {
		clearTimeout(timeout.current);
		timeout.current = setTimeout(() => {
			if (e)
				dispatch(getLocations({ search: e || '', page: 1, limit: 99 }));
		}, 300);
	};

	const handleOnSelect = (data, option) => {
		const { value, building_floor_id } = option || {};
		let query = { id: value };

		if (building_floor_id && GeneralHelper.isObjectId(building_floor_id))
			query = { id: value, building_floor_id };

		const queryString = GeneralHelper.objectToQueryString(query);
		history.push(`/location/id?${queryString}`);
	};

	// ANCHOR useEffects
	useEffect(() => {
		dispatch(getAllBaseMap());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Street view draws the panorama graph of the floor on screen, so it is keyed on the floor and
	// not on the toggle: switching from 2/F to 3/F with the mode already on has to re-fetch, or the
	// map keeps showing 2/F's 360 nodes and edges over 3/F's plan.
	useEffect(() => {
		if (!streetViewMode || !activeBuildingFloorId) return;

		dispatch(getPanoramaNodes({ building_floor_id: activeBuildingFloorId }));
		dispatch(getPanoramaEdges({ building_floor_id: activeBuildingFloorId }));
	}, [streetViewMode, activeBuildingFloorId, dispatch]);

	// 360 coverage is being built up floor by floor, so most floors have none yet. Turning the mode
	// on there would leave the map looking untouched, with nothing to say why — so once this floor's
	// answer is actually in and it is empty, say so and drop back out of the mode.
	// The same goes for a drag: the person can still be dropped, but the user hears straight away
	// that there is nothing on this floor to drop it on.
	useEffect(() => {
		if (!showPanoramaCoverage || loading_panorama_nodes) return;
		// The fetch for the floor on screen has not landed yet — `panorama_nodes` still describes
		// whichever floor was asked for last.
		if (panorama_nodes_building_floor_id !== activeBuildingFloorId) return;
		if (transformedPanoramaNodes && transformedPanoramaNodes.length) return;

		message.open({
			key: NO_PANORAMA_MESSAGE_KEY,
			type: 'info',
			content: 'No 360° view available on this floor',
			// Clear of the search field and the category chips above the map on mobile.
			style: { marginTop: 112 }
		});
		if (streetViewMode) setStreetView(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showPanoramaCoverage, loading_panorama_nodes, panorama_nodes_building_floor_id, activeBuildingFloorId, transformedPanoramaNodes]);

	// Following ends as soon as the map shows anything but the page it was following from - another
	// floor, room or building, a search, directions. Coming back to that page by hand does not
	// resume it; tapping "locate me" does. Without this the map would be taken off whatever the user
	// is browsing every time positioning, which is always on, sees them change level.
	useEffect(() => {
		const followed = followedFloorRef.current;
		if (!followed) return;
		if (location.pathname === followed.pathname && query.id === followed.id && query.building_floor_id === followed.building_floor_id) return;

		followedFloorRef.current = null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, location.search]);

	// The user changed level while the map is following them: show the new floor's plan. Without
	// this the blue dot is filtered out — HKUSTMap only draws it when the displayed floor matches the
	// user's. Keyed on the floor, not the position, so the fixes in between leave the map alone.
	useEffect(() => {
		const followed = followedFloorRef.current;
		const floorId = userLocation?.building_floor_id;
		if (!followed || !GeneralHelper.isObjectId(floorId) || followed.building_floor_id === floorId) return;

		showUserFloor(userLocation);
		// No flyTo here: the zoom the user had is kept (see fitToFloorPlan in HKUSTMap).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userLocation?.building_floor_id]);

	useEffect(() => {
		if (!userLocation) return;

		// The fix the user tapped for has landed: open their floor, and centre on them once its plan
		// is on screen. Only the awaited one - the position keeps streaming at 1 Hz afterwards, and
		// recentring on every update would make the map impossible to browse.
		if (pendingLocateRef.current) {
			pendingLocateRef.current = false;
			firstFixHandledRef.current = true;
			showUserFloor(userLocation);
			setCentreOnUser(true);
			return;
		}

		// The first fix positioning found on its own. Show the user where they are the way a maps app
		// opens on your position - but wide, on the campus map already in front of them: no page is
		// opened and the zoom is kept. Only on the untouched home screen, and only while the camera is
		// still where the app put it; whatever the user opened, followed a link to or panned to
		// comes first.
		if (!firstFixHandledRef.current) {
			firstFixHandledRef.current = true;
			if (isUntouchedHome() && mapRef.current.panToUserLocation) {
				mapRef.current.panToUserLocation(getVisibleMapOffset());
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userLocation]);

	// Centre on the user, once their floor's plan is on screen — loading the geojson
	// fits the bounds of the whole floor, overriding an earlier flyTo.
	useEffect(() => {
		if (!centreOnUser) return;

		const floorId = userLocation?.building_floor_id;
		if (GeneralHelper.isObjectId(floorId) && buildingFloor?._id !== floorId) return;

		setCentreOnUser(false);
		// Get the bottom drawer out of the way first, so the zoomed-in dot lands on
		// visible map rather than behind the sheet.
		collapseDrawer();
		if (mapRef.current && mapRef.current.flyToUserLocation) {
			mapRef.current.flyToUserLocation();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [centreOnUser, buildingFloor, userLocation?.building_floor_id]);

	useEffect(() => {
		const pathnames = location.pathname.split('/').filter(part => part);
		const findPathInPaths = (paths, path) => paths.find(item => item === path);

		const target_path = findPathInPaths(pathnames, 'directions');
		setShowLocationSearch(!Boolean(target_path));
	}, [location]);

	useEffect(() => {
		if (all_base_map && all_base_map.features && all_base_map.features.length > 0) {
			const centroid = turf.centroid(JSON.parse(all_base_map));
			setLngLat(centroid.geometry.coordinates);
		} else {
			setLngLat(MapDefaults.defaultCentroid);
		}
	}, [all_base_map]);

	useEffect(() => {
		const pathnames = location.pathname.split('/').filter(part => part);
		if (locationId) {
			setLocationCoordinates(null);
			const queryObject = { ...query, id: locationId };
			const queryString = GeneralHelper.objectToQueryString(queryObject);
			history.push(`${pathnames.length ? location.pathname : '/location/id'}?${queryString}`);
		}

		if (pointOfInterestId) {
			setLocationCoordinates(null);
			const queryObject = { ...query, id: pointOfInterestId };
			const queryString = GeneralHelper.objectToQueryString(queryObject);
			history.push(`${pathnames.length ? location.pathname : '/location/id'}?${queryString}`);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locationId, pointOfInterestId]);

	useEffect(() => {
		if (!building_floor) {
			setBuildingFloor({});
			return;
		}

		setBuildingFloor(state => state._id !== building_floor._id ? building_floor : state);
	}, [building_floor]);

	useEffect(() => {
		if (buildingFloor && buildingFloor?.geojson) {
			const centroid = turf.centroid(buildingFloor.geojson);
			setLngLat(centroid.geometry.coordinates);
		}
	}, [buildingFloor]);

	useEffect(() => {
		setLocationCoordinates(null);
		if (directions && directions?.paths && directions.paths.length) {
			const queryString = GeneralHelper.objectToQueryString({
				...query,
				// id: directions.start_location_id,
				building_floor_id: directions.start_location_building_floor_id
			});

			history.push(`${location.pathname}?${queryString}`);
			return;
		}
		else if (location_details) {
			if (location_details.longitude && location_details.latitude) {
				setLocationCoordinates([parseFloat(location_details.longitude), parseFloat(location_details.latitude)]);
				setLngLat([parseFloat(location_details.longitude), parseFloat(location_details.latitude)]);
			}

			if (location_details?.building_floor_id && (directions && !directions?.paths))
				history.push(`${location.pathname}?${GeneralHelper.objectToQueryString({ ...query, building_floor_id: location_details.building_floor_id })}`);
		}
	}, [location_details, directions]);

	useEffect(() => {
		const pathnames = location.pathname.split('/').filter(part => part);

		if (location_details && (location_details?.default_floor_id || location_details?.building_floor_id)) {
			// This fires on *opening* a location, but `location_details` also arrives on a plain
			// remount — coming back from the 360 view, or following a link — where it is the store
			// handing back the location already on screen. A building carries only its default
			// floor, so acting on that would answer "back from a 4/F panorama" with the G/F plan.
			// The floor in the URL is the user's choice; it is only replaced when the location
			// itself changes.
			const isRestoredOnMount = floorAppliedForRef.current === null;
			const isSameLocation = floorAppliedForRef.current === location_details._id;
			floorAppliedForRef.current = location_details._id;

			// Opening a building lands on its default floor — except when we opened it to show
			// the user where they are, in which case that default (usually G) would immediately
			// replace the floor they are actually standing on. Checked only after the location has
			// been recorded above: otherwise the next building the user opens looks like a remount,
			// and keeps this building's floor id in the URL instead of getting its own floor.
			if (query.building_floor_id && query.building_floor_id === followedFloorRef.current?.building_floor_id) return;

			if ((isRestoredOnMount || isSameLocation) && GeneralHelper.isObjectId(query.building_floor_id)) return;

			const queryObject = { ...query, id: location_details._id, building_floor_id: location_details?.building_floor_id || location_details?.default_floor_id };
			const queryString = GeneralHelper.objectToQueryString(queryObject);
			const target = `${pathnames.length ? location.pathname : '/location/id'}?${queryString}`;
			// Already on the floor this would open, so pushing would only stack a duplicate entry
			// that the back button then has to be pressed twice to get past.
			if (target === `${location.pathname}${location.search}`) return;
			history.push(target);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location_details]);

	// 360 coverage belongs to one building's floors, so the mode cannot follow the user into
	// another building: its graph would be drawn over a plan they never turned it on for, and the
	// toggle would come up already lit on a building they have just arrived at. Moving between
	// floors, or to another room of the same building, leaves it alone.
	//
	// Deliberately after the floor effect above: both run in the same commit when `location_details`
	// changes, and this one has to write last so it rewrites the URL that effect just pushed.
	useEffect(() => {
		const buildingId = location_details?.building_id;
		if (!buildingId) return;

		const previousBuildingId = shownBuildingRef.current;
		shownBuildingRef.current = buildingId;

		// No previous building means we have only just arrived — a first load or a remount coming
		// back from the 360 view — which is not the user changing building.
		if (!streetViewMode || !previousBuildingId || previousBuildingId === buildingId) return;

		setStreetView(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location_details, streetViewMode]);

	useEffect(() => {
		setTempRoutes(null);
		if (!directions || !directions.paths) {
			return;
		};

		if (GeneralHelper.isObjectId(query?.building_floor_id) && (query?.step && !isNaN(query?.step))) {
			const step = parseInt(query.step);
			const { paths } = directions || {};

			if (paths && (paths[parseInt(step) - 1] && paths[parseInt(step) - 1].properties)) {
				if (query.building_floor_id === paths[parseInt(step) - 1].properties?.building_floor_id) {
					const { geometry } = paths[parseInt(step) - 1] || {};
					if (geometry)
						setTempRoutes(geometry.coordinates);
				}
			}
		}
	}, [directions, query?.building_floor_id, query?.step]);
	// ANCHOR end useEffects

	const renderMapControls = () => {
		// Compact on phones, where the controls float over a small map. Kept the same size as the
		// floor selector's buttons (`.floor-selector` in index.less).
		const controlSize = isMobile ? 40 : 58;
		const controlStyle = { width: controlSize, height: controlSize };
		const controlGap = isMobile ? 8 : 12;

		return (
			<div className="map-control">
				<Flex
					gap={24}
					style={{ height: '100%', alignItems: 'flex-end' }}
				>
					<div style={{ flex: 1 }}>
						{
							((location_details && location_building_floors)) &&
							<FloorSelector
								className="hkust floors"
								floors={location_building_floors}
								total={location_building_floors.length}
								onChange={handleSwitchFloor}
							/>
						}
					</div>
					<div>
						<Space
							size={controlGap}
							direction="vertical"
							style={{ marginInlineStart: 'auto', alignSelf: 'flex-end' }}
						>
							{/* <Button
										size="large"
										type="default"
										styles={{ icon: { color: '#0088FF' } }}
										icon={
											<FontAwesomeIcon
												icon={faStreetView}
												fontSize={20}
											/>}
										style={{ width: 58, height: 58, border: 'unset', boxShadow: '0 0px 24px -12px #000000AA' }}
									/> */}
							{/* Indoor positioning ("locate me"). Sits directly above the reset-orientation
								button and is only shown in a native shell that declares the
								capability - older shells wrapped this same web app without the SDK. */}
							{
								positioningAvailable &&
								<Space
									size={controlGap}
									direction="vertical"
									style={{ boxShadow: '0 0px 24px -12px #000000AA' }}
								>
									<Button
										size="large"
										onClick={handleLocateMe}
										type="default"
										// Never disabled — being disconnected from the positioning
										// server is exactly when a retry has to be possible. The
										// spinner replaces the icon while we detect.
										loading={positioningLoading}
										styles={{ icon: { color: userLocation ? '#1A73E8' : '#666666' } }}
										icon={
											<FontAwesomeIcon
												icon={faLocationCrosshairs}
												fontSize={22}
											/>}
										style={controlStyle}
										title="Show my location"
									/>
								</Space>
							}
							{
								location_details &&
								<Space
									size={controlGap}
									direction="vertical"
									style={{ boxShadow: '0 0px 24px -12px #000000AA' }}
								>
									<Button
										size="large"
										onClick={() => mapRef.current.handleResetOrientation()}
										type="default"
										styles={{ icon: { color: '#0088FF' } }}
										icon={
											<img
												src={resetNorthIcon}
												alt="Reset Map"
												style={{ width: 20, height: 20, verticalAlign: 'middle' }}
											/>
										}
										style={controlStyle}
									/>
								</Space>
							}
							{
								!isMobile && location_details &&
								<Space.Compact
									direction="vertical"
									style={{ boxShadow: '0 0px 24px -12px #000000AA' }}
								>
									<Button
										size="large"
										onClick={() => handleZoom(1, locationCoordinates)}
										type="default"
										styles={{ icon: { color: '#0088FF' } }}
										icon={
											<FontAwesomeIcon
												icon={faPlus}
												fontSize={20}
											/>}
										style={controlStyle}
									/>
									<Button
										size="large"
										onClick={() => handleZoom(-1, locationCoordinates)}
										type="default"
										styles={{ icon: { color: '#0088FF' } }}
										icon={
											<FontAwesomeIcon
												icon={faMinus}
												fontSize={20}
											/>}
										style={controlStyle}
									/>
								</Space.Compact>
							}
							{/* 360 view. Shown on any floor plan; floors without imagery are handled
								on toggle rather than by hiding the button, which would mean asking
								the API for every floor a user merely looks at. Tap to show the floor's
								360 points; drag the person out onto one to open it. */}
							{
								location_details &&
								<Space
									size={controlGap}
									direction="vertical"
									style={{ boxShadow: '0 0px 24px -12px #000000AA' }}
								>
									<StreetViewPegman
										active={streetViewMode}
										mapRef={mapRef}
										nodes={transformedPanoramaNodes}
										style={controlStyle}
										iconSize={isMobile ? 24 : 30}
										onToggle={handleStreetViewClick}
										onDragStart={handlePegmanDragStart}
										onDragEnd={() => setPegmanDragging(false)}
										onDrop={node => openPanorama(node._id)}
										onMiss={handlePegmanMiss}
									/>
								</Space>
							}
						</Space>
					</div>
				</Flex>
			</div>
		);
	};

	return (
		<IndoorPositioningProvider value={positioning}>
			<div className="hkust layout">
				<aside
					ref={contentPaneRef}
					className={`content-pane ${isMobile ? 'transparent' : 'opaque'}`}
				>
					{
						!isMobile &&
						<img
							src={hkustLogoLabel}
							alt="HKUST Logo"
							className="logo"
						/>
					}

					{
						showLocationSearch &&
						<LocationSearch
							className="hkust location-seach"
							loading={loading || loading_details}
							value={location_details ? (location_details.name && location_details.building_short_name ? `${location_details.name}, ${location_details.building_short_name}` : location_details.full_name) : null}
							onClear={handleGoBack}
							hasResults={location_details || locations || filteredLocations}
							nullish={!location_details && !locations && !filteredLocations && !buildings}
							options={locations}
							onSelect={handleOnSelect}
							handleSelectOnChange={handleSelectOnChange}
						/>
					}

					{children}
				</aside>
				<div className="viewport">
					<div
						ref={pointOfInterestsRef}
						className="point-of-interests"
					>
						{
							showLocationSearch &&
							<PointOfInterestSelector
								className="hkust poi"
								onIconClick={handlePoiClick}
								loadingLocType={loadingLocType}
							/>
						}
					</div>
					{/* Map controls always render so the indoor-positioning ("locate me") button
						stays visible; the floor selector, reset-orientation, zoom and 360 controls
						inside remain gated by `location_details`. */}
					{renderMapControls()}

					<div style={{ height: '100%', width: '100%' }}>
						<HKUSTMap
							loading={loading_building_floor || loading_directions}
							locationCoordinates={locationCoordinates}
							geojson={buildingFloor?.geojson}
							cadGeojson={buildingFloor?.cad_geojson}
							baseGeojson={all_base_map}
							navNodes={showPanoramaCoverage ? transformedPanoramaNodes : (nav_nodes || point_of_interests)}
							navEdges={showPanoramaCoverage ? transformedPanoramaEdges : tempRoutes}
							zoom={isMobile ? 16 : 15}
							initialCenter={isMobile ? [114.262986, 30] : undefined}
							ref={mapRef}
							locationId={location_details?._id}
							mapOnClick={handleMapOnClick}
							baseMapOnClick={handleBaseMapOnClick}
							className="hkust map"
							popupComponent={popupComponent}
							filteredLocations={filteredLocations}
							keyword={keyword}
							disableAutoZoom={showPanoramaCoverage}
							hideEdgeArrows={showPanoramaCoverage}
							userLocation={userLocation}
							userHeading={userHeading}
							activeFloorId={query.building_floor_id}
						/>
					</div>
				</div>
			</div >
		</IndoorPositioningProvider>
	);
};

export default MapLayout;
